/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { SearchOutlined, ExpandOutlined, LockOutlined, CalendarOutlined, RightOutlined } from "@ant-design/icons";
import { Table, Input, Button, Row, Col, Card, Tabs, Badge, Modal, DatePicker, Typography, Space, message, Popconfirm, Collapse } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { LockingService } from "../../../services/api/locking";
import "./style.scss";

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const LockingManagement = () => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [isExtendModalVisible, setIsExtendModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hubsData, setHubsData] = useState([]);
    const [lockedAwbsSearch, setLockedAwbsSearch] = useState("");
    const [lockedShipmentsData, setLockedShipmentsData] = useState([]);
    const [lockedShipmentsLoading, setLockedShipmentsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} items`,
    });
    const [extensionDate, setExtensionDate] = useState(null);
    const [extensionReason, setExtensionReason] = useState("");
    const [applyLoading, setApplyLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isBulk, setIsBulk] = useState(false);

    const fetchHubsData = async (page = 1, pageSize = 10, search = "") => {
        setLoading(true);
        try {
            const params = {
                page: page,
                limit: pageSize,
                search: search
            };
            const response = await LockingService.getAllHubsPendingAwbs(params);

            if (response && response.hubs) {
                setHubsData(response.hubs.map(hub => ({
                    ...hub,
                    key: hub.hub_id || `hub-${Math.random()}`,
                    cpCode: hub.hub_id || "---",
                    cpName: hub.hub_name || "---",
                    pendingAwbs: hub.total_pending_awbs !== undefined ? hub.total_pending_awbs : "---",
                    activeLocks: (hub.rules || []).filter(r => r.is_locked).length
                })));
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                    total: response.total_hubs || 0
                }));
            } else {
                setHubsData([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            console.error("Error fetching hubs data:", error);
            message.error("Failed to fetch hubs data");
            setHubsData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHubsData(pagination.current, pagination.pageSize, searchText);
    }, []);

    const handleSearch = () => {
        fetchHubsData(1, pagination.pageSize, searchText);
    };



    const handleTableChange = (newPagination) => {
        fetchHubsData(newPagination.current, newPagination.pageSize, searchText);
    };

    const handleLockedShipmentsSearch = async () => {
        if (!lockedAwbsSearch.trim()) {
            message.warning("Please enter at least one AWB number");
            return;
        }

        const awbs = lockedAwbsSearch
            .split(/[\s,\n]+/)
            .filter(awb => awb.trim())
            .join(",");

        if (!awbs) {
            message.warning("Invalid AWB format");
            return;
        }

        setLockedShipmentsLoading(true);
        try {
            const response = await LockingService.getUnbookedShipmentsDetails(awbs);
            console.log("Locked Shipments Raw Response from Service:", response);

            let data = [];
            if (response && response.status === "success" && Array.isArray(response.data)) {
                console.log("Detected { status: 'success', data: [...] } structure");
                data = response.data;
            } else if (Array.isArray(response)) {
                console.log("Detected Array structure");
                data = response;
            } else if (response && Array.isArray(response.data)) {
                console.log("Detected { data: [...] } structure");
                data = response.data;
            } else {
                console.log("Response structure not recognized:", response);
            }

            console.log("Extracted data array length:", data.length);

            if (data.length > 0) {
                const mappedData = data.map((item, index) => {
                    const rowKey = item.awbNumber || item.awb_number || `awb-${index}`;
                    return {
                        key: rowKey,
                        awbNumber: item.awbNumber || item.awb_number || "---",
                        rule: item.ruleId || item.rule_id || item.rule || "---",
                        status: item.status || item.current_status || "---",
                        nearestExpiry: item.nearest_expiry || item.nearestExpiry || "---",
                        hubAtEvent: item.hubAtEvent || item.hub_name || "---",
                        cp_name: item.cp_name || item.cpName || "---",
                        city: item.city || "---",
                        state: item.state || "---",
                        pincode: item.pincode || "---",
                        receivedAt: item.receivedAt || item.created_at || "---"
                    };
                });
                console.log("Final Mapped Data for Table:", mappedData);
                setLockedShipmentsData(mappedData);
                message.success(`Successfully found ${mappedData.length} records`);
            } else {
                setLockedShipmentsData([]);
                message.info("No locked shipments found for the provided AWB(s)");
            }
        } catch (error) {
            console.error("Error in handleLockedShipmentsSearch:", error);
            message.error("An error occurred while fetching locked shipment details");
        } finally {
            setLockedShipmentsLoading(false);
        }
    };

    const showExtendModal = (record) => {
        setIsBulk(false);
        setSelectedRecord(record);
        setIsExtendModalVisible(true);
    };

    const handleBulkExtend = () => {
        if (selectedRows.length === 0) return;
        setIsBulk(true);
        setIsExtendModalVisible(true);
    };

    const handleRemoveShipment = async (awbNumber) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem("userinfo")) || {};
            const payload = {
                awb: [awbNumber],
                reason: "Requested from Locking Management Dashboard",
                requested_by: userInfo.username || userInfo.email || "System User"
            };

            const result = await LockingService.clearUnbookedShipments(payload);
            if (result && result.status === "success") {
                message.success(`Successfully removed AWB: ${awbNumber}`);
                // Refresh the table data
                handleLockedShipmentsSearch();
            } else {
                message.error(result?.message || `Failed to remove AWB: ${awbNumber}`);
            }
        } catch (error) {
            console.error("Error removing shipment:", error);
            message.error("An error occurred while removing the shipment");
        }
    };

    const handleCancel = () => {
        setIsExtendModalVisible(false);
        setSelectedRecord(null);
        setExtensionDate(null);
        setExtensionReason("");
        setIsBulk(false);
    };

    const handleApply = async () => {
        if (!extensionDate || !extensionReason.trim()) {
            message.warning("Please select a date and provide a reason");
            return;
        }

        if (!isBulk && !selectedRecord) return;
        if (isBulk && selectedRows.length === 0) return;

        setApplyLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem("userinfo")) || {};
            const today = moment().startOf("day");
            const selectedDate = moment(extensionDate).startOf("day");
            const extensionDays = selectedDate.diff(today, "days");

            let cpDetails = [];
            if (isBulk) {
                // Focus ONLY on the Hubs selected in the CURRENT visible page (page-wise requirement)
                // This ensures a single API call for the batch visible to the user.
                const currentPageSelected = hubsData.filter(h => selectedRowKeys.includes(h.key));
                cpDetails = currentPageSelected.map(row => ({
                    cp_id: row.cpCode,
                    rule_ids: (row.rules || []).map(r => r.rule_id)
                }));
            } else {
                cpDetails = [
                    {
                        cp_id: selectedRecord.cpCode,
                        rule_ids: (selectedRecord.rules || []).map(r => r.rule_id)
                    }
                ];
            }

            if (cpDetails.length === 0) {
                message.warning("No Hubs selected for extension");
                setApplyLoading(false);
                return;
            }

            const payload = {
                cp_details: cpDetails,
                extension_days: extensionDays > 0 ? extensionDays : 0,
                extension_hours: 0,
                reason: extensionReason,
                requested_by: userInfo.username || userInfo.email || "System User"
            };

            const response = await LockingService.applySelectiveExtension(payload);

            // Robust success check: if response exists and status isn't explicitly false/failed
            const isSuccess = response && (
                response.status === "success" ||
                (typeof response.status === "string" && (
                    response.status.toLowerCase().includes("applied") ||
                    response.status.toLowerCase().includes("success")
                )) ||
                response.message?.toLowerCase().includes("success") ||
                response.message?.toLowerCase().includes("applied")
            );

            if (isSuccess) {
                message.success("Extension Successful Added");
                setIsExtendModalVisible(false);
                setExtensionDate(null);
                setExtensionReason("");
                setIsBulk(false);
                setSelectedRowKeys([]);
                setSelectedRows([]);
                fetchHubsData(pagination.current, pagination.pageSize, searchText);
            } else {
                // If it reached here without throwing, but isSuccess is false,
                // we should still consider if we should close the modal.
                // However, usually the service throws if status is false.
                message.error(response?.message || response?.status || "Failed to apply extension");
            }
        } catch (error) {
            console.error("Error applying extension:", error);
            message.error(error?.message || "An error occurred while applying the extension");
        } finally {
            setApplyLoading(false);
        }
    };

    const columns = [
        {
            title: "Hub Code",
            dataIndex: "cpCode",
            key: "cpCode",
            render: (text) => <strong>{text || "---"}</strong>,
        },
        {
            title: "Hub Name",
            dataIndex: "cpName",
            key: "cpName",
            render: (text) => text || "---",
        },
        {
            title: "Pending Trips",
            dataIndex: "pendingAwbs",
            key: "pendingAwbs",
            render: (text, record) => {
                const count = parseInt(text) || 0;
                return count > 0 ? (
                    <a
                        style={{ textDecoration: "underline", color: "#1890ff" }}
                        onClick={() => navigate(`/pending-awbs?cp_id=${record.cpCode}&cp_name=${record.cpName}`)}
                    >
                        {text}
                    </a>
                ) : (
                    <span style={{ color: "#8c8c8c" }}>{text}</span>
                );
            },
        },
        {
            title: "Active Locks",
            dataIndex: "activeLocks",
            key: "activeLocks",
            render: (count) => (
                <Badge
                    count={count}
                    style={{
                        backgroundColor: count > 0 ? "#ff4d4f" : "#52c41a",
                        boxShadow: "0 0 0 1px #d9d9d9 inset"
                    }}
                />
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<ExpandOutlined />}
                    className="extend-locks-btn"
                    onClick={() => showExtendModal(record)}
                >
                    Extend Locks
                </Button>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);

            // Maintain a persistent list of selected row objects across pages
            setSelectedRows(prevSelectedRows => {
                // Create a map of existing selected rows by their key, filtering out any accidental undefineds
                const selectionMap = new Map(
                    prevSelectedRows
                        .filter(row => row && row.key)
                        .map(row => [row.key, row])
                );

                // Add newly selected rows from the current page (filtering out any undefined rows)
                if (rows) {
                    rows.forEach(row => {
                        if (row && row.key) {
                            selectionMap.set(row.key, row);
                        }
                    });
                }

                // Remove rows that are no longer in keys (unselected)
                const finalRows = [];
                if (keys) {
                    keys.forEach(key => {
                        if (selectionMap.has(key)) {
                            finalRows.push(selectionMap.get(key));
                        }
                    });
                }

                return finalRows;
            });
        },
        onSelectAll: async (selected) => {
            if (selected) {
                if (pagination.total > hubsData.length) {
                    // User wants to select ALL records across all pages
                    setLoading(true);
                    try {
                        const params = {
                            page: 1,
                            limit: pagination.total,
                            search: searchText
                        };
                        const response = await LockingService.getAllHubsPendingAwbs(params);
                        if (response && response.hubs) {
                            const allHubs = response.hubs.map(hub => ({
                                ...hub,
                                key: hub.hub_id || `hub-${Math.random()}`,
                                cpCode: hub.hub_id || "---",
                                cpName: hub.hub_name || "---",
                                pendingAwbs: hub.total_pending_awbs !== undefined ? hub.total_pending_awbs : "---",
                                activeLocks: (hub.rules || []).filter(r => r.is_locked).length,
                                rules: hub.rules || []
                            }));
                            const allKeys = allHubs.map(h => h.key);
                            setSelectedRowKeys(allKeys);
                            setSelectedRows(allHubs);
                            message.success(`Selected ${hubsData.length} hubs on this page`);
                        }
                    } catch (error) {
                        console.error("Error selecting all records:", error);
                        message.error("Failed to select all records");
                    } finally {
                        setLoading(false);
                    }
                } else {
                    // Just select the current page records
                    const currentPageKeys = hubsData.map(h => h.key);
                    setSelectedRowKeys(currentPageKeys);
                    setSelectedRows(hubsData);
                    message.success(`Selected ${hubsData.length} hubs on this page`);
                }
            } else {
                setSelectedRowKeys([]);
                setSelectedRows([]);
            }
        },
        preserveSelectedRowKeys: true,
    };

    const lockedShipmentsColumns = [
        {
            title: "Reference Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            render: (text) => (
                <a
                    style={{ textDecoration: "underline", color: "#1890ff", fontWeight: "500" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </a>
            )
        },
        { title: "Rule", dataIndex: "rule", key: "rule" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <div className="status-tag-locked">
                    {status}
                </div>
            )
        },
        { title: "Nearest Expiry", dataIndex: "nearestExpiry", key: "nearestExpiry" },
        { title: "Hub (At Event)", dataIndex: "hubAtEvent", key: "hubAtEvent" },
        { title: "Received At", dataIndex: "receivedAt", key: "receivedAt" },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to remove this locked shipment?"
                    onConfirm={() => handleRemoveShipment(record.awbNumber)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button type="primary" danger className="remove-btn-locked">
                        Remove
                    </Button>
                </Popconfirm>
            )
        },
    ];

    return (
        <div className="locking-management-page">
            <Tabs defaultActiveKey="1" className="locking-tabs">
                <TabPane tab="Extend Locks" key="1">
                    <Card className="table-card">
                        <Row gutter={16} className="table-actions">
                            <Col xs={24} sm={12} md={8}>
                                <Input
                                    placeholder="Search Hub Name, Hub Code"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                    value={searchText}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSearchText(val);
                                        if (!val) {
                                            fetchHubsData(1, pagination.pageSize, "");
                                        }
                                    }}
                                    onPressEnter={handleSearch}
                                    className="search-input"
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    className="action-btn search-btn"
                                    onClick={handleSearch}
                                >
                                    Search
                                </Button>
                            </Col>


                            {hubsData.filter(h => selectedRowKeys.includes(h.key)).length > 0 && (
                                <Col>
                                    <Button
                                        type="primary"
                                        icon={<ExpandOutlined />}
                                        className="action-btn bulk-extend-action-btn"
                                        onClick={handleBulkExtend}
                                    >
                                        Bulk Extend ({hubsData.filter(h => selectedRowKeys.includes(h.key)).length} HUBs)
                                    </Button>
                                </Col>
                            )}
                        </Row>

                        <Table
                            rowSelection={{
                                type: "checkbox",
                                ...rowSelection,
                            }}
                            columns={columns}
                            dataSource={hubsData}
                            loading={loading}
                            pagination={pagination}
                            onChange={handleTableChange}
                            className="locking-table"
                        />
                    </Card>
                </TabPane>
                <TabPane tab="Locked Operations" key="2">
                    <Card className="locked-shipments-card">
                        <Row gutter={16} className="search-row-locked" align="middle">
                            <Col flex="auto">
                                <Input
                                    placeholder="Enter one or more reference numbers separated by comma, space, or new line"
                                    className="locked-search-input"
                                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                                    value={lockedAwbsSearch}
                                    onChange={(e) => setLockedAwbsSearch(e.target.value)}
                                    onPressEnter={handleLockedShipmentsSearch}
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    className="big-search-btn"
                                    onClick={handleLockedShipmentsSearch}
                                    loading={lockedShipmentsLoading}
                                >
                                    Search
                                </Button>
                            </Col>
                        </Row>

                        <Table
                            columns={lockedShipmentsColumns}
                            dataSource={lockedShipmentsData}
                            loading={lockedShipmentsLoading}
                            locale={{ emptyText: "No data" }}
                            className="locking-table-v2"
                            pagination={{
                                size: "small",
                                total: lockedShipmentsData.length,
                                showSizeChanger: false,
                                showTotal: (total) => `Total ${total} Shipments`,
                                position: ["bottomRight"]
                            }}
                        />
                    </Card>
                </TabPane>
            </Tabs>

            <Modal
                title={
                    <div className="modal-header-custom">
                        <Space>
                            <div className="lock-icon-wrapper">
                                <LockOutlined />
                            </div>
                            <Title level={4} style={{ margin: 0 }}>
                                {isBulk ? "Bulk Extend Lock Dates" : "Extend Locks"}
                            </Title>
                        </Space>
                    </div>
                }
                open={isExtendModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancel" onClick={handleCancel} className="modal-footer-btn cancel-btn">
                        Cancel
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        onClick={handleApply}
                        className="modal-footer-btn apply-btn"
                        disabled={applyLoading || !extensionDate || !extensionReason.trim()}
                    >
                        {isBulk ? "Update All Dates" : "Apply"}
                    </Button>,
                ]}
                width={800}
                className="extend-locks-modal"
            >
                {isBulk ? (
                    <div className="modal-content-wrapper header-section">
                        <Text type="secondary" className="bulk-selection-count">
                            Previewing {hubsData.filter(h => selectedRowKeys.includes(h.key)).length} of {selectedRowKeys.length} selected Channel Partners
                        </Text>
                    </div>
                ) : (
                    selectedRecord && (
                        <div className="modal-content-wrapper">
                            <Text type="secondary" className="sub-title">
                                {selectedRecord.cpName || "---"} ({selectedRecord.cpCode || "---"})
                            </Text>
                        </div>
                    )
                )}

                <div className="modal-content-wrapper" style={{ marginTop: isBulk ? "24px" : "0px" }}>
                    <Row gutter={24}>
                        <Col span={10}>
                            <div className="form-item-wrapper">
                                <label className="custom-label">
                                    Select Extend Date <span className="required">*</span>
                                    <Text type="secondary" className="hint"> (max 4 days from today)</Text>
                                </label>
                                <DatePicker
                                    placeholder="Select date to extend (required)"
                                    style={{ width: "100%" }}
                                    suffixIcon={<CalendarOutlined />}
                                    className="custom-datepicker"
                                    value={extensionDate}
                                    onChange={(date) => setExtensionDate(date)}
                                    disabledDate={(current) => {
                                        // Can only select from today to 4 days from today
                                        const today = moment().startOf("day");
                                        const maxDay = moment().add(4, "days").endOf("day");
                                        return current && (current < today || current > maxDay);
                                    }}
                                />
                            </div>
                        </Col>
                        <Col span={14}>
                            <div className="form-item-wrapper">
                                <label className="custom-label">
                                    Reason for Extension <span className="required">(required)</span>
                                </label>
                                <TextArea
                                    placeholder="Enter reason for extending locks..."
                                    className="custom-input custom-textarea"
                                    value={extensionReason}
                                    onChange={(e) => setExtensionReason(e.target.value)}
                                    rows={1}
                                    autoSize={{ minRows: 1, maxRows: 3 }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {isBulk && (
                        <div className="bulk-cp-list-section">
                            <Collapse
                                ghost
                                expandIcon={({ isActive }) => <RightOutlined rotate={isActive ? 90 : 0} />}
                                className="custom-collapse"
                            >
                                {(hubsData.filter(h => selectedRowKeys.includes(h.key)) || []).map((row) => (
                                    <Panel
                                        header={
                                            <div className="cp-header-content">
                                                <span className="cp-name-code">
                                                    {row.cpName} <span className="cp-code">({row.cpCode})</span>
                                                </span>
                                                <span className="restricted-badge">Restricted</span>
                                            </div>
                                        }
                                        key={row.key}
                                        className="custom-panel"
                                    >
                                        <div className="bulk-inner-locks-section">
                                            <div className="inner-section-header">
                                                <Title level={5} className="inner-title">ALL LOCKS</Title>
                                                <Badge count={row.rules?.length || 0} showZero color="#ff4d4f" className="inner-count-badge" />
                                            </div>
                                            <Row gutter={[12, 12]} className="inner-locks-grid">
                                                {(row.rules || []).map((rule, idx) => (
                                                    <Col span={12} key={idx}>
                                                        <div className="inner-lock-card">
                                                            <div className="inner-lock-card-header">
                                                                <div className="rule-info">
                                                                    <LockOutlined className="icon-tiny" />
                                                                    <Text className="rule-name" ellipsis={{ tooltip: rule.rule_id }}>
                                                                        {rule.rule_id || "---"}
                                                                    </Text>
                                                                </div>
                                                                <span className={`inner-status-tag ${rule.is_locked ? "active" : ""}`}>
                                                                    {rule.is_locked ? "Active" : "Inactive"}
                                                                </span>
                                                            </div>
                                                            {extensionDate && (
                                                                <div className="date-transition-preview">
                                                                    <span className="prev-date">{moment().format("DD-MMM-YYYY")}</span>
                                                                    <span className="arrow">→</span>
                                                                    <span className="next-date">{extensionDate.format("DD-MMM-YYYY")}</span>
                                                                </div>
                                                            )}
                                                            <DatePicker
                                                                disabled
                                                                value={extensionDate || moment()}
                                                                format="DD-MMM-YYYY"
                                                                className="inner-card-datepicker"
                                                                suffixIcon={<CalendarOutlined />}
                                                            />
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    </Panel>
                                ))}
                            </Collapse>
                        </div>
                    )}

                    {!isBulk && selectedRecord && (
                        <div className="bulk-inner-locks-section" style={{ marginTop: "24px", border: "1px solid #f0f0f0" }}>
                            <div className="inner-section-header">
                                <Title level={5} className="inner-title">ALL LOCKS</Title>
                                <Badge count={selectedRecord.rules?.length || 0} showZero color="#ff4d4f" className="inner-count-badge" />
                            </div>

                            <Row gutter={[12, 12]} className="inner-locks-grid">
                                {(selectedRecord.rules || []).map((rule, idx) => (
                                    <Col span={8} key={idx}>
                                        <div className="inner-lock-card" style={{ maxWidth: "200px" }}>
                                            <div className="inner-lock-card-header">
                                                <div className="rule-info">
                                                    <LockOutlined className="icon-tiny" />
                                                    <Text className="rule-name" ellipsis={{ tooltip: rule.rule_id }}>
                                                        {rule.rule_id || "---"}
                                                    </Text>
                                                </div>
                                                <span className={`inner-status-tag ${rule.is_locked ? "active" : ""}`}>
                                                    {rule.is_locked ? "Active" : "Inactive"}
                                                </span>
                                            </div>
                                            {extensionDate && (
                                                <div className="date-transition-preview">
                                                    <span className="prev-date">{moment().format("DD-MMM-YYYY")}</span>
                                                    <span className="arrow">→</span>
                                                    <span className="next-date">{extensionDate.format("DD-MMM-YYYY")}</span>
                                                </div>
                                            )}
                                            <DatePicker
                                                disabled
                                                value={extensionDate || moment()}
                                                format="DD-MMM-YYYY"
                                                className="inner-card-datepicker"
                                                suffixIcon={<CalendarOutlined />}
                                            />
                                        </div>
                                    </Col>
                                ))}
                                {(!selectedRecord.rules || selectedRecord.rules.length === 0) && (
                                    <Col span={24}>
                                        <Text type="secondary">No active locks found for this hub.</Text>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default LockingManagement;
